'use client';

import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import { NotebookPen, RotateCw, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { trackEvent } from '@/lib/event-tracking';
import type { PlannedMeal } from '@/store/ai-planner';

// Key to track if swipe hint has been shown in this session
const SWIPE_HINT_SHOWN_KEY = 'ahafood.ai-planner.swipe-hint-shown';

interface MealPlanCardProps {
  index?: number;
  meal: PlannedMeal;
  // onOrder?: () => void;
  onCancel?: () => void;
  onSwape?: () => void;
  // onViewDetail?: () => void;
}

const STATUS_CONFIG = {
  upcoming: {
    iconBg: 'bg-white/10',
    iconColor: 'text-white',
  },
  passed: {
    iconBg: 'bg-[#ff8200]',
    iconColor: 'text-white',
  },
  cancelled: {
    iconBg: 'bg-red-50',
    iconColor: 'text-white',
  },
} as const;

export function MealPlanCard({
  index,
  meal,
  // onOrder,
  onCancel,
  onSwape,
  // onViewDetail,
}: MealPlanCardProps) {
  const statusConfig = STATUS_CONFIG[meal.status ?? 'upcoming'];
  const isPast = meal.status === 'passed';
  const isCancelled = meal.status === 'cancelled';
  const canOrder = meal.status === 'upcoming';

  // Swipe state
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isShowingHint, setIsShowingHint] = useState(false);
  const startXRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // Swipe hint animation for first card (2 cycles in ~3s)
  useEffect(() => {
    // Only show hint for first card (index === 0) and if can order
    if (index !== 0 || !canOrder) return;

    // Check if hint was already shown in this session
    const hintShown = sessionStorage.getItem(SWIPE_HINT_SHOWN_KEY);
    if (hintShown) return;

    const timeouts: NodeJS.Timeout[] = [];

    // Delay before starting hint animation
    const startDelay = setTimeout(() => {
      setIsShowingHint(true);
      sessionStorage.setItem(SWIPE_HINT_SHOWN_KEY, 'true');

      // Cycle 1: Swipe right
      timeouts.push(
        setTimeout(() => {
          setSwipeX(200);
        }, 0)
      );

      // Cycle 1: Swipe back
      timeouts.push(
        setTimeout(() => {
          setSwipeX(0);
        }, 600)
      );
      // End hint animation
      timeouts.push(
        setTimeout(() => {
          setIsShowingHint(false);
        }, 1000)
      );
    }, 500);

    return () => {
      clearTimeout(startDelay);
      timeouts.forEach(clearTimeout);
    };
  }, [index, canOrder]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!canOrder) return;
      startXRef.current = e.touches[0].clientX;
      setIsSwiping(true);
    },
    [canOrder]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isSwiping || !canOrder) return;
      const currentX = e.touches[0].clientX;
      const diff = currentX - startXRef.current;

      if (diff > 0) {
        setSwipeX(diff);
      }
    },
    [isSwiping, canOrder]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping || !canOrder) return;
    setIsSwiping(false);

    const cardWidth = cardRef.current?.offsetWidth ?? 0;
    const threshold = cardWidth * 0.5;

    if (swipeX > threshold) {
      setSwipeX(cardWidth);
      setTimeout(() => {
        onCancel?.();
        setSwipeX(0);
      }, 300);
    } else {
      setSwipeX(0);
    }
  }, [isSwiping, swipeX, canOrder, onCancel]);

  return (
    <div className="relative flex gap-3" ref={cardRef}>
      {/* Timeline Dot */}
      <div className="flex flex-col items-center pt-1.5">
        <div
          className={cn(
            'size-10 rounded-full flex items-center justify-center shrink-0 transition-all',
            statusConfig.iconBg
          )}
        >
          {/* <div className={statusConfig.iconColor}>{statusConfig.icon}</div> */}
        </div>
        {/* Vertical Line */}
        <div className="w-[2px] flex-1 bg-white/10 mt-2" />
      </div>

      {/* Card Content */}
      <div className="flex-1 pb-6 bg-transparent">
        {/* Date Header */}
        <div className="flex items-center gap-2 mb-3">
          <svg
            className="size-4 text-white/60"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-white/60 text-sm font-medium uppercase">
            {meal.deliveryTime ?? 'Thời gian chưa xác định'}
          </span>
        </div>

        {/* Swipe Background */}
        {canOrder && (
          <div
            className="absolute top-[34px] bottom-6 left-14 right-0 bg-gradient-to-r from-red-50/90 to-red-60/80 rounded-2xl flex items-center justify-start px-6"
            style={{
              opacity: swipeX > 0 ? Math.min(swipeX / 100, 1) : 0,
            }}
          >
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/20 rounded-full">
                <X className="size-5 text-white" />
              </div>
              <span className="text-white font-bold text-base">Bỏ qua</span>
            </div>
          </div>
        )}

        {/* Meal Card */}
        <Card
          className={cn(
            'relative overflow-hidden transition-all duration-300 cursor-pointer rounded-2xl !ai-planner-button',
            'border border-white/10',
            'backdrop-blur-xl',
            'hover:border-[#ff8200]/50',
            (isPast || isCancelled) && 'opacity-60'
          )}
          style={{
            transform: `translateX(${swipeX}px)`,
            transition: isSwiping
              ? 'none'
              : isShowingHint
                ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                : 'transform 0.3s ease-out, all 0.3s ease',
          }}
          // onClick={onViewDetail}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="p-3">
            {/* Image */}
            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-3">
              <Image
                src={
                  meal.imageUrl ||
                  `${assetPrefix}/static/images/empty-store-bot.webp`
                }
                alt={meal.name}
                fill
                className={cn('object-cover', isCancelled && 'grayscale')}
              />

              {/* Refresh Icon for Upcoming */}
              {canOrder && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwape?.();
                    trackEvent('meal_plan', {
                      event_name: 'click_button',
                      button_name: 'meal_plan_swap',
                    });
                  }}
                  className="absolute bottom-2 right-2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all hover:scale-110"
                >
                  <RotateCw className="size-4 text-neutral-80" />
                </button>
              )}
            </div>

            {/* Info */}
            <div>
              <h4
                className={cn(
                  'font-semibold text-white/80 text-base mb-2 line-clamp-1',
                  isCancelled && 'line-through text-white/40'
                )}
              >
                {meal.name}
              </h4>

              {/* Price */}
              <p className="text-white text-base font-semibold mb-2">
                ~{formatCurrency(meal.estimatedPrice ?? 0)}
                {/* {formatCurrency(meal.priceMin)} -{' '}
                {formatCurrency(meal.priceMax)} */}
              </p>

              {/* Meta Info */}
              {meal.user_note ? (
                <div className="flex items-center gap-1.5 text-white/40 text-xs">
                  <NotebookPen className="size-3.5" />
                  <span>{meal.user_note}</span>
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// Skeleton for loading state
export function MealPlanCardSkeleton() {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center pt-1.5">
        <div className="size-10 rounded-full bg-white/10 animate-pulse" />
        <div className="w-[2px] flex-1 bg-white/10 mt-2" />
      </div>

      <div className="flex-1 pb-6">
        <div className="h-4 w-32 bg-white/10 rounded animate-pulse mb-3" />
        <Card className="bg-white/5 border border-white/10 overflow-hidden">
          <div className="p-3">
            <div className="w-full aspect-[4/3] rounded-xl bg-white/10 animate-pulse mb-3" />
            <div className="h-4 w-3/4 bg-white/10 rounded animate-pulse mb-2" />
            <div className="h-4 w-1/2 bg-white/10 rounded animate-pulse mb-2" />
            <div className="h-3 w-2/3 bg-white/10 rounded animate-pulse" />
          </div>
        </Card>
      </div>
    </div>
  );
}
